import type {
    SapUiGenericAppPageSettings as Settings,
    ChangeDefinition,
    DeprecatedProperties,
    ExtensionLogger,
    ImportObjectPageV2Parameters,
    ObjectPageSectionData,
    SyncRule,
    Manifest
} from '@sap/ux-specification-types';
import {
    ControlType,
    LogSeverity,
    v2,
    ACTIONTITLEPREFIX,
    PageTypeV2,
    DefinitionName,
    PropertyName,
    SchemaTag,
    UIVOCABULARYALPHADOT,
    VOCWITHSLASH
} from '@sap/ux-specification-types';
import type { FlexChangesForTransfer, FlexForTransfer } from '../controls/table';
import { addFlexChangeToConfigTable, addFlexChangeToConfigTableColumn } from '../controls/table';
import { log } from '../../../../extensionLogger';
import {
    findObjectPageV2,
    findCustomSectionExtensions,
    checkV2Page,
    getV2Pages,
    flexChangeMatchesTemplateAndEntitySet
} from '../utils';
import type {} from '../../../common';
import {
    DEFINITION_LINK_PREFIX,
    convertActionKeyToStableId,
    getTargetFromCustomColumnRef,
    getReflectMetadata,
    getSectionTitle,
    getTextForDeprecated,
    parseChangeString
} from '../../../common';
import { ensureManifestSectionByPathV2 } from '../../export/manifest';
import i18next from 'i18next';
import { initI18n } from '../../../../i18n/i18n';
import { MetadataInstanceFactoryV2 as Factory } from '../../export/factory';
import type { ObjectPageV2ConfigParameters, SectionPartProperties } from '../../types';
import {
    ActionTypeV2,
    SectionTypeV2,
    OP_STANDARD_FOOTER_ACTION_PREFIX,
    OP_STANDARD_HEADER_ACTION_PREFIX
} from '../../types';
import { UIAnnotationTerms } from '@sap-ux/vocabularies-types/vocabularies/UI';
import { convertSelector, addFragments, convertActionKey } from '../common';
import type { Definition, DefinitionOrBoolean } from 'typescript-json-schema';

// Comprises list of flexChanges that do not require a facet id
const supportedFlexChangesWithoutFacetId = [ControlType.Avatar, ControlType.ObjectPageLayout, ControlType.DynamicPage];

/**
 * Determines Facet ID from single string selector Id.
 *
 * @param selectorIdParts - Parts of the selector ID split by '::'
 * @param facetID - The facet ID to be determined
 * @returns {string} FacetID
 */
function getSingleSelectorIdPart(selectorIdParts: string[], facetID: string) {
    const lastIdParts = selectorIdParts[0].split('--');
    const id = lastIdParts[lastIdParts.length - 1];
    if (selectorIdParts[0].indexOf('::action::') >= 0 || selectorIdParts[0].startsWith('action::')) {
        // Object Page ToolBar Actions
        facetID =
            lastIdParts[0].split('::action::')[0] ||
            lastIdParts[0].split(`${UIAnnotationTerms.LineItem}::`)[1].split('::')[0];
        facetID = `${ACTIONTITLEPREFIX}${facetID}`;
    } else if (
        selectorIdParts[0].indexOf(UIAnnotationTerms.LineItem) >= 0 ||
        selectorIdParts[0].indexOf('UI.LineItem') >= 0 ||
        selectorIdParts[0].indexOf('::Table') >= 0
    ) {
        facetID = lastIdParts[0].split('::Table')[0].split('::responsiveTable')[0];
    } else {
        const idParts = id.split('::');
        if (idParts.length > 1) {
            facetID = idParts[0];
        } else if (lastIdParts[0].indexOf('::') > -1) {
            facetID = lastIdParts[0].split('::')[0];
        }
    }
    return facetID;
}

/**
 * Determines Facet ID of flex change.
 *
 * @param {string} selectorId Control ID of flex change
 * @returns {string} FacetID
 */
function getFacetID(selectorId: string): string {
    let facetID: string;
    const selectorIdParts: string[] = selectorId.split(':::');
    if (selectorIdParts.length === 1) {
        facetID = getSingleSelectorIdPart(selectorIdParts, facetID);
    } else if (selectorId.indexOf('sSmartTableId') >= 0) {
        for (const element of selectorIdParts) {
            if (element.startsWith('sSmartTableId')) {
                facetID = element
                    .split('sSmartTableId::')[1]
                    .split('::Table')[0]
                    .split('/responsiveTable')[0]
                    .replace(`::${UIAnnotationTerms.LineItem}`, `/@${UIAnnotationTerms.LineItem}`);
                break;
            }
        }
    } else {
        facetID = selectorIdParts[0];
    }
    return facetID;
}

/**
 * Prepares the list of flex changes.
 *
 * @param changes - Array of flex change strings to be processed
 * @param entitySet - The entity set associated with the object page
 * @param logger - Optional logger instance for logging errors or warnings
 * @returns {FlexChangesForTransfer} - list of prepared flex changes
 */
function prepareFlexChanges(changes: string[], entitySet: string, logger?: ExtensionLogger): FlexChangesForTransfer {
    const result: FlexChangesForTransfer = {};
    const complexIdMatch = /.+DataField.+:::sSmartTableId::.+/;
    changes.forEach((changeString) => {
        try {
            const change = parseChangeString(changeString) as ChangeDefinition;
            const changeId = (change && change.controlId?.split('--')) || [];
            if (
                change &&
                flexChangeMatchesTemplateAndEntitySet(change, v2.SAPUI5_VIEW_EXTENSION_OBJECT_PAGE, entitySet)
            ) {
                let selectorId = changeId[1].replace(/:3a:3a/g, '::');
                if (complexIdMatch.exec(selectorId) !== null) {
                    selectorId = selectorId.split('template:::TableColumn:::')[1];
                }
                const facetID: string = getFacetID(selectorId);
                selectorId = convertSelector(selectorId)
                    .replace(/:3a:3a/g, '::')
                    .replace(/\/@com.sap.vocabularies/g, '::com.sap.vocabularies');
                result[`${selectorId}::${change.content.property}`] = {
                    controlId: selectorId,
                    propertyId: change.content.property,
                    facetId: facetID,
                    newValue:
                        change.content.newBinding !== undefined ? change.content.newBinding : change.content.newValue,
                    controlType: change.controlType,
                    creation: change.creation
                };
            }
        } catch (error) {
            log(
                logger,
                {
                    severity: LogSeverity.Error,
                    message: i18next.t('FLEXERROR')
                },
                ...error
            );
        }
    });
    const sortedList = Object.entries(result).sort(
        (a: [string, any], b: [string, any]) => a[1].creation - b[1].creation
    );
    return sortedList.reduce((accum, [k, v]) => {
        accum[k] = v;
        return accum;
    }, {});
}

/**
 * Returns the type of a given section.
 *
 * @param schemaSection - current section
 * @returns - type of section, differentiated by level (Sub/Section)
 */
const getSectionType = (schemaSection: object): SectionTypeV2 => {
    let reference;
    if (schemaSection['$ref']) {
        reference = schemaSection['$ref'].includes('<') ? schemaSection['$ref'].split('<')[0] : schemaSection['$ref'];
        return reference.replace(DEFINITION_LINK_PREFIX, '');
    } else if (schemaSection['properties']?.['subsections']?.['$ref']) {
        reference = schemaSection['properties']['subsections']['$ref'];
        reference = reference.includes('<') ? reference.split('<')[0] : reference;
        return reference.replace(DEFINITION_LINK_PREFIX, '');
    } else if (schemaSection['properties']?.['subsections']) {
        return SectionTypeV2.SubSection;
    }
};

/**
 * Checks for each column if the given flexChange should be applied.
 *
 * @param {Definition} columnDefinition - generic column definition in the app schema
 * @param {FlexForTransfer} flexChange - flex change definition
 * @param columns - list of all coliumns (properties of the table definition in schema)
 * @param configPart - part of the configuration to be filled
 */
function addFlexChangeToColumns(
    columnDefinition: Definition,
    flexChange: FlexForTransfer,
    columns: { [key: string]: DefinitionOrBoolean },
    configPart: { [key: string]: any }
) {
    if (Object.keys(columnDefinition.properties).some((propertyName) => propertyName === flexChange.propertyId)) {
        for (const column in columns) {
            if (column === 'custom') {
                //custom is a placeholder for custom columns that cannot have flex changes
                continue;
            }
            addFlexChangeToConfigTableColumn(configPart, flexChange, column);
        }
    }
}

/**
 * Import of flex changes of a table to the config JSON of OP.
 *
 * @param flexChanges - list of flex changes (pre-processed by prepareFlexChanges)
 * @param propertyKey - key of the current property in the app schema
 * @param configPart - part of the configuration to be filled
 * @param sectionPart - structure comprising the section information
 * @param lineItemsDefinition - generic line items definition in the app schema
 * @param columnDefinition - generic column definition in the app schema
 */
function importFlexChangesOfTable(
    flexChanges: FlexChangesForTransfer,
    propertyKey: string,
    configPart: { [key: string]: any },
    sectionPart: SectionPartProperties,
    lineItemsDefinition?: Definition,
    columnDefinition?: Definition
) {
    const facetIds = sectionPart.title
        ? [sectionPart.title.replace(/@/g, '')]
        : [sectionPart.id.replace(/@/g, ''), sectionPart.id?.replace(/::@com.sap.vocabularies/g, VOCWITHSLASH)];

    //Check all flex changes
    for (const selector in flexChanges) {
        const flexChange = flexChanges[selector];
        const propertyIdMatch = flexChange.propertyId === propertyKey;
        const isTableColumn = propertyKey === 'columns' && flexChange.controlType === ControlType.TableColumn;
        //Only consider flex changes for table or table columns of the given facet
        if ((propertyIdMatch || isTableColumn) && facetIds.indexOf(flexChange?.facetId) > -1) {
            if (columnDefinition) {
                //Check if flex change is for any column
                addFlexChangeToColumns(columnDefinition, flexChange, lineItemsDefinition.properties, configPart);
            } else {
                //Check if the flex change is for the table
                addFlexChangeToConfigTable(configPart, flexChange, lineItemsDefinition);
            }
        }
    }
}

/**
 * Helper function to evaluate if flex change is for standard action.
 *
 * @param flexChange - flex change
 * @param controlIdMatch - if ID of the control matches RegEx
 * @returns {boolean} is standard action flex change
 */
function isStandardActionFlexChange(flexChange: FlexForTransfer, controlIdMatch: boolean) {
    return flexChange.facetId === 'template' && controlIdMatch;
}

/**
 * Helper function to check if Flex change Id and config/schema Id match.
 *
 * @param changeId Control Id of flex change to compare against
 * @param targetId Schema/Section Id
 * @param checkTitle flag if check consists of schema title
 * @returns {boolean} if flex change id and config/schema Id match
 */
const idElementChecker = (changeId: string[], targetId: string[], checkTitle = false): boolean => {
    if (!checkTitle) {
        return targetId.every((v) => changeId.includes(v));
    }
    // Check that qualifier matches too
    const startIndex = changeId.indexOf(targetId[0]);
    const endIndex = changeId.length - startIndex > targetId.length ? changeId.length - 1 : undefined;
    const arrayToCheck = changeId.slice(startIndex, endIndex);
    return arrayToCheck.length === targetId.length && targetId.every((v) => arrayToCheck.includes(v));
};

/**
 * RegEx check of the control Id and/or the section Id against the selector of a flex change.
 *
 * @param schemaDefinition - current section of the app specific schema
 * @param flexChange - flex change
 * @param controlIdChecker - RegEx for the controlId
 * @param selectionIdChecker - RegEx for the selectionId
 * @param sectionId - title of the section (optional)
 * @param controlId - ID of the given control, for e.g. GroupElement, Action (optional)
 * @returns the result of all checks
 */
function matchSelector(
    schemaDefinition: Definition,
    flexChange: FlexForTransfer,
    controlIdChecker: RegExp,
    selectionIdChecker: RegExp,
    sectionId?: string,
    controlId?: string
): boolean {
    let idMatch = false,
        controlIdMatch = false;

    if (controlId) {
        idMatch = controlIdMatch = controlIdChecker.test(flexChange.controlId);
        if (sectionId) {
            idMatch = controlIdMatch && selectionIdChecker.test(flexChange.controlId);
        }
    } else if (sectionId) {
        idMatch = selectionIdChecker.test(flexChange.controlId);
    }
    const controlIdParts = flexChange.controlId.split('::');

    const isGroupId = flexChange.controlType === ControlType.GroupElement && controlIdMatch;
    const isActionId = flexChange.facetId?.includes(ACTIONTITLEPREFIX) && !sectionId && idMatch;
    const isSectionId = sectionId && idElementChecker(controlIdParts, sectionId.split('::')) && idMatch;
    const isSchemaTitle =
        schemaDefinition.title && idElementChecker(controlIdParts, schemaDefinition.title.split('::'), true);
    const isSupportedWithoutFacetId =
        !flexChange.facetId && supportedFlexChangesWithoutFacetId.includes(flexChange.controlType as ControlType);
    const isStandardAction = isStandardActionFlexChange(flexChange, controlIdMatch);
    return isGroupId || isActionId || isSectionId || isSchemaTitle || isSupportedWithoutFacetId || isStandardAction;
}

/**
 * Import of (regular) flex changes to the config JSON of OP.
 *
 * @param schemaDefinition - current section of the app specific schema
 * @param configPart - part of the configuration to be filled
 * @param flexChanges - list of flex changes (pre-processed by prepareFlexChanges)
 * @param propertyKey - key of the current property in the app schema
 * @param sectionId - title of the section (optional)
 * @param elementId - stable ID part of the given element, for e.g. GroupElement, Action (optional)
 */
function importFlexChanges(
    schemaDefinition: Definition,
    configPart: { [key: string]: any },
    flexChanges: FlexChangesForTransfer,
    propertyKey: string,
    sectionId?: string,
    elementId?: string
) {
    const controlIdChecker = new RegExp(`.*${elementId}`);
    const selectionIdChecker = new RegExp(`.*${sectionId}`);

    for (const selector in flexChanges) {
        const flexChange = flexChanges[selector];
        //Property match?
        if (flexChange.propertyId !== propertyKey) {
            continue;
        }
        //Control type match?
        if (flexChange.controlType !== schemaDefinition.properties[flexChange.propertyId]?.[SchemaTag.controlType]) {
            continue;
        }
        const match = matchSelector(
            schemaDefinition,
            flexChange,
            controlIdChecker,
            selectionIdChecker,
            sectionId,
            elementId
        );
        if (match) {
            configPart[propertyKey] = flexChange.newValue;
        }
    }
}

/**
 * Checks for deprecated properties and writes them to the log.
 *
 * @param manifestSection - given section in the manifest
 * @param deprecatedProperties - list of deperecated properties
 * @param {ImportObjectPageV2Parameters} importParameters  - object comprising all input data
 * @param manifestPath - manifest path, as calcualted by the sync rule
 */
function checkDeprecatedProperties(
    manifestSection: object,
    deprecatedProperties: DeprecatedProperties,
    importParameters: ImportObjectPageV2Parameters,
    manifestPath: string
) {
    if (manifestSection && deprecatedProperties) {
        for (const key in manifestSection) {
            const deprecated = deprecatedProperties[key];
            if (deprecated) {
                log(importParameters.logger, {
                    severity: LogSeverity.Warning,
                    message: getTextForDeprecated(key, deprecated),
                    location: {
                        path: 'webapp/manifest.json/',
                        range: manifestPath.split('/')
                    }
                });
                delete deprecatedProperties[key];
            }
        }
    }
}

/**
 * Imports manifest settings to the object page config.
 *
 * @param syncRule - sync rule for accessing the reflect-metadata
 * @param pageKeys - list of page keys (comprises the one entry describing the given page)
 * @param importParameters  - object comprising all input data
 * @param configPart - part of the configuration to be filled
 * @param propertyKey  - key of the current property of the schema definition
 * @param deprecatedProperties - list od deprecated properties
 * @param sectionPart - structure comprising the section information
 * @returns {object} - the section in the manifest
 */
function importManifestSetting(
    syncRule: SyncRule,
    pageKeys: string[],
    importParameters: ImportObjectPageV2Parameters,
    configPart: { [key: string]: any },
    propertyKey: string,
    deprecatedProperties: DeprecatedProperties,
    sectionPart?: SectionPartProperties
) {
    const sectionIdInManifest = sectionPart?.id
        ? sectionPart.id.replace(/\/@/g, '::').replace(/@com.sap/g, 'com.sap')
        : undefined;
    let manifestPath: string;
    let manifestSection: object;

    //In cas of OP sections, the title is used as a key in the manifest, if defined
    if (sectionPart?.title) {
        manifestPath = syncRule.manifest.path(pageKeys, {} as Manifest, sectionPart.title);
        manifestSection = ensureManifestSectionByPathV2(importParameters.manifest, manifestPath);
        if (Object.values(manifestSection).length === 0) {
            manifestPath = syncRule.manifest.path(pageKeys, {} as Manifest, sectionIdInManifest);
            manifestSection = ensureManifestSectionByPathV2(importParameters.manifest, manifestPath);
        }
    } else {
        manifestPath = syncRule.manifest.path(pageKeys, {} as Manifest, sectionIdInManifest);
        manifestSection = ensureManifestSectionByPathV2(importParameters.manifest, manifestPath);
    }
    const importHandler = syncRule.manifest.import;
    if (importHandler && typeof importHandler === 'function') {
        const value = importHandler(manifestSection, sectionPart?.id);
        if (value !== undefined) {
            configPart[propertyKey] = value;
        }
    } else {
        const manifestKey = syncRule.manifest.key || propertyKey;
        if (manifestKey in manifestSection) {
            configPart[propertyKey] = manifestSection[manifestKey];
        }
    }
    // Check for deprecated properties.
    checkDeprecatedProperties(manifestSection, deprecatedProperties, importParameters, manifestPath);
    return manifestSection;
}

/**
 * Transfers settings from manifest and flexChanges to object page config.
 *
 * @param {ImportObjectPageV2Parameters} importParameters  - object comprising all input data
 * @param configPart - part of the configuration to be filled
 * @param schemaDefinition - current section of the app specific schema
 * @param configParameters - comprises page keys and flex changes
 * @param sectionPart - structure comprising the section information
 * @param elementId - stable ID part of the given element (action, column, ...) (optional)
 */
function transferSettingsAndFlexChanges(
    importParameters: ImportObjectPageV2Parameters,
    configPart: { [key: string]: any },
    schemaDefinition: Definition,
    configParameters: ObjectPageV2ConfigParameters,
    sectionPart?: SectionPartProperties,
    elementId?: string
): void {
    if (!schemaDefinition) {
        return;
    }
    const deprecatedProperties: DeprecatedProperties = configPart?.getDeprecated ? configPart.getDeprecated() : {};
    const columnDef = (importParameters.jsonSchema as Definition).definitions.TableColumn as Definition;

    //Loop on all properties of the schema definition
    for (const propertyKey in schemaDefinition.properties || schemaDefinition) {
        const syncRule = getReflectMetadata(configPart, propertyKey);
        if (propertyKey === 'columns' || syncRule?.flex) {
            if (
                sectionPart?.type?.startsWith('ObjectPageSectionTableV2') ||
                sectionPart?.type?.startsWith('ObjectPageSubSectionTableV2')
            ) {
                if (propertyKey === 'columns') {
                    //Flex changes of section or subsection table columns
                    const lineItemRef =
                        schemaDefinition.properties[propertyKey]['$ref'].split(DEFINITION_LINK_PREFIX)[1];
                    const lineItemDefinition = (importParameters.jsonSchema as Definition).definitions[
                        lineItemRef
                    ] as Definition;
                    importFlexChangesOfTable(
                        configParameters.flexChanges,
                        propertyKey,
                        configPart,
                        sectionPart,
                        lineItemDefinition,
                        columnDef
                    );
                } else {
                    //Flex changes of section table or subsection table
                    importFlexChangesOfTable(
                        configParameters.flexChanges,
                        propertyKey,
                        configPart,
                        sectionPart,
                        schemaDefinition
                    );
                }
            } else {
                //Flex changes of section
                const facetId = sectionPart?.title || sectionPart?.id;
                importFlexChanges(
                    schemaDefinition,
                    configPart,
                    configParameters.flexChanges,
                    propertyKey,
                    facetId,
                    elementId
                );
            }
        } else if (syncRule?.manifest) {
            //Manifest settings
            importManifestSetting(
                syncRule,
                configParameters.pageKeys,
                importParameters,
                configPart,
                propertyKey,
                deprecatedProperties,
                sectionPart
            );
        }
    }
}

/**
 *
 * @param actionType - type of action
 * @param configActionId - id of action in config
 * @param importTypeId - type of action that was be initiated
 * @param facetId - The ID of the facet to be used for importing flex changes
 * @returns {string} actionId - to be used for import of flex changes
 */
function getActionId(actionType: string, configActionId: string, importTypeId: string, facetId: string): string {
    let actionId = convertActionKeyToStableId(configActionId);
    switch (actionType) {
        case ActionTypeV2.Form: {
            actionId = `action::${actionId}::${facetId}::${importTypeId}`;
            break;
        }
        case ActionTypeV2.OPFooter: {
            actionId = `action::${actionId}::Determining`;
            break;
        }
        case ActionTypeV2.OPToolBar: {
            actionId = actionId.includes('sAction::')
                ? `action::${actionId.split('sAction::')[1]}`
                : `action::${actionId.replace(':2f', '::')}`;
            break;
        }
        case ActionTypeV2.OPHeaderStandard: {
            actionId = `${OP_STANDARD_HEADER_ACTION_PREFIX}${actionId}`;
            break;
        }
        case ActionTypeV2.OPFooterStandard: {
            actionId = `${OP_STANDARD_FOOTER_ACTION_PREFIX}${actionId}`;
            break;
        }
    }
    return actionId.replace(/@/g, '');
}

/**
 * Adds standard actions to the corresponding header/footer config.
 *
 * @param config actions config
 * @param actions existing actions definition
 * @param importParameters - Object comprising all input data for the import process
 * @param configParameters - Object containing configuration parameters such as page keys, flex changes, and factory instance
 */
function fillStandardActions(
    config,
    actions: Definition,
    importParameters: ImportObjectPageV2Parameters,
    configParameters: ObjectPageV2ConfigParameters
) {
    for (const action in actions) {
        const actionType = actions[action].$ref.split(DEFINITION_LINK_PREFIX)[1].split('<')[0];
        if ([ActionTypeV2.OPHeaderStandard, ActionTypeV2.OPFooterStandard].includes(actionType)) {
            const actionRef = actions[action].$ref.split('<')?.[1].split('>')[0];
            const actionId = getActionId(actionType, action, '', '');
            config.actions[action] = configParameters.factory.createInstance(PageTypeV2.ObjectPage, actionRef, {});
            transferSettingsAndFlexChanges(
                importParameters,
                config[PropertyName.actions][action],
                (importParameters.jsonSchema as Definition).definitions[actionRef] as Definition,
                configParameters,
                {},
                actionId
            );
        }
    }
}

/**
 *  Fills the action of the configuration.
 *
 * @param {ImportObjectPageV2Parameters} importParameters - object comprising all input data
 * @param {object} actionProperties - current properties' list (of action)
 * @param {object} configPart - the given part of the object page config
 * @param {ObjectPageV2ConfigParameters} configParameters - container for factory, pageKeys, flexChanges, v2Page, ObjectPageConfig
 * @param {ActionTypeV2|string} importTypeId - type of action that should be initiated
 * @param facetId - The ID of the facet to be used for importing flex changes
 */
function fillActions(
    importParameters: ImportObjectPageV2Parameters,
    actionProperties: object,
    configPart: object,
    configParameters: ObjectPageV2ConfigParameters,
    importTypeId: ActionTypeV2 | string,
    facetId?: string
): void {
    configPart[PropertyName.actions] = {};
    const actionType = importTypeId.split('::').pop();
    if (configParameters.flexChanges && Object.keys(configParameters.flexChanges).length > 0) {
        const sectionPart: SectionPartProperties = {};
        if (facetId) {
            sectionPart.id = facetId;
        }
        for (const actionProperty in actionProperties) {
            const configActionId = convertActionKey(actionProperty);
            const actionId = getActionId(actionType, configActionId, importTypeId, facetId);
            if (actionId) {
                configPart[PropertyName.actions][configActionId] = configParameters.factory.createInstance(
                    PageTypeV2.ObjectPage,
                    actionType
                );
                transferSettingsAndFlexChanges(
                    importParameters,
                    configPart[PropertyName.actions][configActionId],
                    (importParameters.jsonSchema as Definition).definitions[actionType] as Definition,
                    configParameters,
                    sectionPart,
                    actionId
                );
            }
            if (
                configPart[PropertyName.actions]?.[configActionId] &&
                Object.keys(configPart[PropertyName.actions][configActionId]).length === 0
            ) {
                delete configPart[PropertyName.actions][configActionId];
            }
        }
    }
    fillStandardActions(configPart, actionProperties, importParameters, configParameters);
    if (Object.keys(configPart[PropertyName.actions]).length === 0) {
        delete configPart[PropertyName.actions];
    }
}

/**
 *  Fills the fields of an object page form.
 *
 * @param importParameters - object comprising all input data
 * @param sectionPart - current properties' list of form section
 * @param fieldProperties - current properties' list (of fields)
 * @param configPart - the given part of the object page config
 * @param configParameters - container for factory, pageKeys, flexChanges, v2Page, ObjectPageConfig
 */
function fillFields(
    importParameters: ImportObjectPageV2Parameters,
    sectionPart: SectionPartProperties,
    fieldProperties: object,
    configPart: object,
    configParameters: ObjectPageV2ConfigParameters
): void {
    let fieldId: string;
    configPart[sectionPart.id]['form']['fields'] = {};
    for (const fieldProperty in fieldProperties) {
        fieldId = `${fieldProperty}::GroupElement`;
        configPart[sectionPart.id]['form']['fields'][fieldProperty] = configParameters.factory.createInstance(
            PageTypeV2.ObjectPage,
            'Field'
        );
        const elementId = sectionPart.title?.includes(UIVOCABULARYALPHADOT)
            ? `${sectionPart.id.replace(/@/g, '')}::${fieldId}`
            : `${sectionPart.title}::${fieldId}`;
        transferSettingsAndFlexChanges(
            importParameters,
            configPart[sectionPart.id]['form']['fields'][fieldProperty],
            (importParameters.jsonSchema as Definition).definitions[DefinitionName.Field] as Definition,
            configParameters,
            {},
            elementId
        );
        if (Object.keys(configPart[sectionPart.id]['form']['fields'][fieldProperty]).length === 0) {
            delete configPart[sectionPart.id]['form']['fields'][fieldProperty];
        }
    }
    if (Object.keys(configPart[sectionPart.id]['form']['fields']).length === 0) {
        delete configPart[sectionPart.id]['form']['fields'];
    }
}

/**
 * Adjusts the app schema by choosing the right table definition, depending on the table type.
 *
 * @param tableType - the type of the given table
 * @param {SectionPartProperties} sectionPart - current properties' list
 * @param schemaDefinitions - definitions' section of the app schema
 * @returns the right schema definition for the given table
 */
function prepareTableSchema(
    tableType: string,
    sectionPart: SectionPartProperties,
    schemaDefinitions: Definition
): Definition {
    let tableSchema: object;
    if (tableType === 'TreeTable' || tableType === 'AnalyticalTable' || tableType === 'GridTable') {
        tableSchema = schemaDefinitions[`ObjectPage${tableType}<${sectionPart.id}>`] as Definition;
    } else if (
        schemaDefinitions[`ObjectPageResponsiveTableWithMultiSelect<${sectionPart.id}>`] &&
        schemaDefinitions[`ObjectPageResponsiveTableWithInlineDelete<${sectionPart.id}>`]
    ) {
        tableSchema = JSON.parse(
            JSON.stringify(schemaDefinitions[`ObjectPageResponsiveTableWithMultiSelect<${sectionPart.id}>`])
        );
        tableSchema['properties'] = {
            ...tableSchema['properties'],
            ...schemaDefinitions[`ObjectPageResponsiveTableWithInlineDelete<${sectionPart.id}>`]['properties']
        };
    }
    return tableSchema;
}

/**
 *  Fills the table section of the object page config.
 *
 * @param {ImportObjectPageV2Parameters} importParameters - object comprising all input data
 * @param {SectionPartProperties} sectionPart - current properties' list
 * @param {Settings} manifestSettings - object page settings section of the manifest
 * @param {object} configPart - the given part of the object page config
 * @param {ObjectPageV2ConfigParameters} configParameters - container for factory, pageKeys, flexChanges, v2Page, ObjectPageConfig
 */
function fillTableInConfig(
    importParameters: ImportObjectPageV2Parameters,
    sectionPart: SectionPartProperties,
    manifestSettings: Settings,
    configPart: object,
    configParameters: ObjectPageV2ConfigParameters
): void {
    const manifestKey = sectionPart.title || sectionPart.id.replace('@com.sap', 'com.sap');
    const manifestSection = manifestSettings && manifestSettings.sections && manifestSettings.sections[manifestKey];

    if (manifestSection || configParameters.flexChanges) {
        const tableType: string = manifestSection?.tableSettings?.type || 'ResponsiveTable';

        //Instantiate metadata
        const opTable = configParameters.factory.createInstance(PageTypeV2.ObjectPage, `ObjectPage${tableType}`, {
            toolBar: {}
        });
        configPart[sectionPart.id] = configParameters.factory.createInstance(PageTypeV2.ObjectPage, sectionPart.type, {
            table: {}
        });
        configPart[sectionPart.id][PropertyName.table] = opTable;

        const schemaDefinitions = (importParameters.jsonSchema as Definition).definitions;

        const tableSchema = prepareTableSchema(tableType, sectionPart, schemaDefinitions);
        //Transfer settings of (sub)section
        transferSettingsAndFlexChanges(
            importParameters,
            configPart[sectionPart.id],
            sectionPart.schema,
            configParameters,
            { title: sectionPart.title }
        );
        //Transfer settings of table
        transferSettingsAndFlexChanges(
            importParameters,
            configPart[sectionPart.id][PropertyName.table],
            tableSchema,
            configParameters,
            sectionPart
        );
        //Transfer settings of table toolbar actions
        const toolBarActions = JSON.parse(JSON.stringify(schemaDefinitions))[
            `Actions<${sectionPart.id}>`
        ] as Definition;
        if (toolBarActions?.properties) {
            const facetId = sectionPart?.title
                ? sectionPart.title.replace(/@/g, '::').replace(/::com/g, 'com')
                : sectionPart?.id?.replace(/@/g, '::').replace(/::com/g, 'com');
            fillActions(
                importParameters,
                toolBarActions.properties,
                configPart[sectionPart.id][PropertyName.table]['toolBar'],
                configParameters,
                ActionTypeV2.OPToolBar,
                facetId
            );
        }
        if (Object.keys(configPart[sectionPart.id][PropertyName.table]['toolBar']).length === 0) {
            delete configPart[sectionPart.id][PropertyName.table]['toolBar'];
        }
    }
}

/**
 * Fills custom columns in a V2 object page config JSON.
 *
 * @param importParameters - object comprising all input data
 * @param escapedSectionId - escaped version of the section ID
 * @param configPart - the given part of the object page config, to be filled
 * @param v2Page - v2 page from manifest.
 * @param sectionId - ID of the given section
 */
function fillCustomColumnsInConfig(
    importParameters: ImportObjectPageV2Parameters,
    escapedSectionId: string,
    configPart: object,
    v2Page: v2.SapUiAppPageV2,
    sectionId: string
): void {
    const customColumnSchemaProp = (
        (importParameters.jsonSchema as Definition).definitions[escapedSectionId] as Definition
    ).properties.custom as Definition;
    const customColumnTarget = customColumnSchemaProp
        ? getTargetFromCustomColumnRef(customColumnSchemaProp.$ref)
        : undefined;
    if (customColumnTarget) {
        const objectPageSectionData: ObjectPageSectionData = {
            key: escapedSectionId.replace('@', ''),
            target: customColumnTarget
        };
        addFragments(
            configPart[escapedSectionId],
            importParameters,
            v2Page,
            PageTypeV2.ObjectPage,
            objectPageSectionData,
            sectionId
        );
    }
}

/**
 *  Fills the sections and subsections of the object page config (recursive function).
 *
 * @param importParameters  - object comprising all input data
 * @param sectionPart - current properties' list of chart section
 * @param manifestSettings - object page settings section of the manifest
 * @param configPart - the given part of the object page config
 * @param configParameters - container for factory, pageKeys, flexChanges, v2Page, ObjectPageConfig
 */
function fillChartSection(
    importParameters: ImportObjectPageV2Parameters,
    sectionPart: SectionPartProperties,
    manifestSettings: Settings,
    configPart: object,
    configParameters: ObjectPageV2ConfigParameters
) {
    const manifestKey = sectionPart.id.replace('@com.sap', 'com.sap');
    const manifestSection = manifestSettings && manifestSettings.sections && manifestSettings.sections[manifestKey];
    configPart[sectionPart.id] = configParameters.factory.createInstance(PageTypeV2.ObjectPage, sectionPart.type, {
        chart: {}
    });
    configPart[sectionPart.id][PropertyName.chart] = configParameters.factory.createInstance(
        PageTypeV2.ObjectPage,
        'ObjectPageChart'
    );
    if (manifestSection || configParameters.flexChanges) {
        transferSettingsAndFlexChanges(
            importParameters,
            configPart[sectionPart.id],
            sectionPart.schema,
            configParameters,
            { title: sectionPart.title }
        );
        transferSettingsAndFlexChanges(
            importParameters,
            configPart[sectionPart.id][PropertyName.chart],
            (importParameters.jsonSchema as Definition).definitions[DefinitionName.ObjectPageChart] as Definition,
            configParameters,
            sectionPart
        );
    }
}

/**
 * Fills the header section of the object page config.
 *
 * @param importParameters - object comprising all input data
 * @param sectionPart - current properties' list of form section
 * @param configPart - the given part of the object page config
 * @param configParameters - container for factory, pageKeys, flexChanges, v2Page, ObjectPageConfig
 */
function fillHeaderSection(
    importParameters: ImportObjectPageV2Parameters,
    sectionPart: SectionPartProperties,
    configPart: object,
    configParameters: ObjectPageV2ConfigParameters
) {
    configPart[sectionPart.id] = configParameters.factory.createInstance(PageTypeV2.ObjectPage, sectionPart.type, {});
    if (configParameters.flexChanges) {
        transferSettingsAndFlexChanges(
            importParameters,
            configPart[sectionPart.id],
            sectionPart.schema,
            configParameters,
            { title: sectionPart.title }
        );
    }
    if (Object.keys(configPart[sectionPart.id]).length === 0) {
        delete configPart[sectionPart.id];
    }
}
/**
 * Fills the form section of the object page config.
 *
 * @param importParameters - object comprising all input data
 * @param sectionPart - current properties' list of form section
 * @param configPart - the given part of the object page config
 * @param configParameters - container for factory, pageKeys, flexChanges, v2Page, ObjectPageConfig
 */
function fillFormSection(
    importParameters: ImportObjectPageV2Parameters,
    sectionPart: SectionPartProperties,
    configPart: object,
    configParameters: ObjectPageV2ConfigParameters
) {
    const formId = sectionPart.schema['properties']['form']['$ref'].split('SectionForm')[1];
    configPart[sectionPart.id] = configParameters.factory.createInstance(PageTypeV2.ObjectPage, sectionPart.type, {
        form: {}
    });
    if (configParameters.flexChanges) {
        transferSettingsAndFlexChanges(
            importParameters,
            configPart[sectionPart.id],
            sectionPart.schema,
            configParameters,
            { title: sectionPart.title }
        );
    }
    const formActions = (importParameters.jsonSchema as Definition).definitions[
        `SectionActions${formId}`
    ] as Definition;
    const facetId = sectionPart?.title
        ? sectionPart.title.replace(/@/g, '::').replace(/::com/g, 'com')
        : sectionPart?.id?.replace(/@/g, '::').replace(/::com/g, 'com');
    if (formActions?.properties) {
        fillActions(
            importParameters,
            formActions.properties,
            configPart[sectionPart.id]['form'],
            configParameters,
            ActionTypeV2.Form,
            facetId
        );
    }
    const formFields = (importParameters.jsonSchema as Definition).definitions[`SectionFields${formId}`] as Definition;
    if (formFields?.properties) {
        fillFields(importParameters, sectionPart, formFields.properties, configPart, configParameters);
    }
    if (Object.keys(configPart[sectionPart.id]['form']).length === 0) {
        delete configPart[sectionPart.id]['form'];
    }
    if (Object.keys(configPart[sectionPart.id]).length === 0) {
        delete configPart[sectionPart.id];
    }
}

/**
 *  Fills the subsection of the object page config.
 *
 * @param importParameters - object comprising all input data
 * @param sectionPart - current properties' list of subsection
 * @param manifestSettings - object page settings section of the manifest
 * @param configPart - the given part of the object page config
 * @param configParameters - container for factory, pageKeys, flexChanges, v2Page, ObjectPageConfig
 */
function fillSubSection(
    importParameters: ImportObjectPageV2Parameters,
    sectionPart: SectionPartProperties,
    manifestSettings: Settings,
    configPart: object,
    configParameters: ObjectPageV2ConfigParameters
) {
    configPart[sectionPart.id] = configParameters.factory.createInstance(PageTypeV2.ObjectPage, sectionPart.type, {
        subsections: {}
    });
    if (configParameters.flexChanges) {
        transferSettingsAndFlexChanges(
            importParameters,
            configPart[sectionPart.id],
            sectionPart.schema,
            configParameters,
            sectionPart
        );
    }
    let currentSchemaSection;
    const schemaProperties = sectionPart.schema.properties;
    if ((schemaProperties?.subsections as Definition)?.properties) {
        currentSchemaSection = (schemaProperties.subsections as Definition).properties;
    } else {
        currentSchemaSection = schemaProperties;
    }

    fillSectionsInConfig(
        importParameters,
        currentSchemaSection,
        manifestSettings,
        configPart[sectionPart.id]['subsections'],
        configParameters
    );
    if (Object.keys(configPart[sectionPart.id]['subsections']).length < 1) {
        delete configPart[sectionPart.id]['subsections'];
    }
}

/**
 * Fills the sections and subsections of the object page config (recursive function).
 *
 * @param {ImportObjectPageV2Parameters} importParameters - object comprising all input data
 * @param {object} sectionProperties - current properties' list (of sections or subsections)
 * @param {Settings} manifestSettings - object page settings section of the manifest
 * @param {object} configPart - the given part of the object page config
 * @param {ObjectPageV2ConfigParameters} configParameters - container for factory, pageKeys, flexChanges, v2Page, ObjectPageConfig.
 */
function fillSectionsInConfig(
    importParameters: ImportObjectPageV2Parameters,
    sectionProperties: object,
    manifestSettings: Settings,
    configPart: object,
    configParameters: ObjectPageV2ConfigParameters
): void {
    const appSchemaDefinitions = (importParameters.jsonSchema as Definition).definitions;
    for (const sectionId in sectionProperties) {
        const escapedSectionId = sectionId.replace(/\/@/g, '::');
        const sectionPart: SectionPartProperties = {
            id: escapedSectionId,
            type: getSectionType(sectionProperties[sectionId]),
            schema: sectionProperties[sectionId],
            title: getSectionTitle(sectionProperties[sectionId], importParameters.jsonSchema)
        };
        if (sectionPart.schema['$ref'] && sectionPart.type) {
            switch (sectionPart.type) {
                case SectionTypeV2.SectionTable:
                case SectionTypeV2.SubSectionTable: {
                    sectionPart.schema = appSchemaDefinitions[`${sectionPart.type}<${sectionId}>`] as Definition;
                    fillTableInConfig(importParameters, sectionPart, manifestSettings, configPart, configParameters);
                    // Custom column ref
                    fillCustomColumnsInConfig(
                        importParameters,
                        escapedSectionId,
                        configPart,
                        configParameters.v2Page,
                        sectionId
                    );
                    break;
                }
                case SectionTypeV2.SectionChart:
                case SectionTypeV2.SubSectionChart: {
                    // Section without subsections
                    sectionPart.schema = appSchemaDefinitions[`${sectionPart.type}<${sectionId}>`] as Definition;
                    fillChartSection(importParameters, sectionPart, manifestSettings, configPart, configParameters);
                    break;
                }
                case SectionTypeV2.SectionForm:
                case SectionTypeV2.SubSectionForm: {
                    sectionPart.schema = appSchemaDefinitions[`${sectionPart.type}<${sectionId}>`] as Definition;
                    fillFormSection(importParameters, sectionPart, configPart, configParameters);
                    break;
                }
                case SectionTypeV2.Section:
                case SectionTypeV2.SectionAddress:
                case SectionTypeV2.SubSectionAddress:
                case SectionTypeV2.SectionContact:
                case SectionTypeV2.SubSectionContact:
                case SectionTypeV2.SubSection:
                case SectionTypeV2.HeaderSection: {
                    // Section with subsections
                    sectionPart.schema = appSchemaDefinitions[`${sectionPart.type}<${sectionId}>`] as Definition;
                    fillSubSection(importParameters, sectionPart, manifestSettings, configPart, configParameters);
                    break;
                }
                case SectionTypeV2.HeaderSectionAddress:
                case SectionTypeV2.HeaderSectionChart:
                case SectionTypeV2.HeaderSectionDataPoint:
                case SectionTypeV2.HeaderSectionForm: {
                    // Header Section
                    sectionPart.schema = appSchemaDefinitions[`${sectionPart.type}<${sectionId}>`] as Definition;
                    fillHeaderSection(importParameters, sectionPart, configPart, configParameters);
                    break;
                }
            }
        } else if (sectionPart.schema.properties?.['subsections']) {
            sectionPart.schema = appSchemaDefinitions[`${sectionPart.type}<${sectionId}>`] as Definition;
            fillSubSection(importParameters, sectionPart, manifestSettings, configPart, configParameters);
        }
    }
}

/**
 * Fills the custom sections in the v2 object page config.
 *
 * @param customSections - list of custom sections, as defined in manifest
 * @param objectPageConfig - the object page configuration (JSON)
 * @param configParameters - object comprising input for filling the config
 * @param schemaDefinitions - definitions in the object page schema
 * @param importParameters  - object comprising API input data
 */
function fillCustomSectionsInConfig(
    customSections: object,
    objectPageConfig: v2.ObjectPageConfigV2,
    configParameters: ObjectPageV2ConfigParameters,
    schemaDefinitions: {
        [key: string]: DefinitionOrBoolean;
    },
    importParameters: ImportObjectPageV2Parameters
): void {
    if (Object.keys(customSections).length > 0) {
        objectPageConfig.sections.custom = [];
        for (const sectionId in customSections) {
            const customSection =
                'fragmentName' in customSections[sectionId]
                    ? configParameters.factory.createInstance(PageTypeV2.ObjectPage, 'ObjectPageCustomSectionFragment')
                    : configParameters.factory.createInstance(PageTypeV2.ObjectPage, 'ObjectPageCustomSectionView');
            const customSectionDefinition =
                schemaDefinitions[
                    'fragmentName' in customSections[sectionId]
                        ? 'ObjectPageCustomSectionFragment'
                        : 'ObjectPageCustomSectionView'
                ];
            transferSettingsAndFlexChanges(
                importParameters,
                customSection,
                customSectionDefinition as Definition,
                configParameters,
                { id: sectionId }
            );
            objectPageConfig.sections.custom.push(customSection);
        }
    }
}

/**
 * Fill properties of object page config based on manifest settings.
 *
 * @param importParameters  - object comprising API input data
 * @param configParameters - object comprising input for filling the config
 * @returns - the object page configuration (JSON)
 */
function fillConfig(
    importParameters: ImportObjectPageV2Parameters,
    configParameters: ObjectPageV2ConfigParameters
): v2.ObjectPageConfigV2 {
    const objectPageConfig: v2.ObjectPageConfigV2 = configParameters.factory.createPageInstance(PageTypeV2.ObjectPage);

    // Transfer page information
    transferSettingsAndFlexChanges(
        importParameters,
        objectPageConfig,
        importParameters.jsonSchema['properties'],
        configParameters
    );
    // Transfer global table settings
    objectPageConfig.globalTableSettings = configParameters.factory.createInstance(
        PageTypeV2.ObjectPage,
        DefinitionName.GlobalTableSettings
    );
    const definitions = (importParameters.jsonSchema as Definition).definitions;
    transferSettingsAndFlexChanges(
        importParameters,
        objectPageConfig.globalTableSettings,
        definitions[DefinitionName.GlobalTableSettings] as Definition,
        configParameters
    );
    if (Object.keys(objectPageConfig.globalTableSettings).length === 0) {
        delete objectPageConfig.globalTableSettings;
    }

    // Transfer header information
    objectPageConfig.header = configParameters.factory.createInstance(
        PageTypeV2.ObjectPage,
        DefinitionName.ObjectPageHeader
    );
    const schemaDefinition = definitions[DefinitionName.ObjectPageHeader]
        ? definitions[DefinitionName.ObjectPageHeader]
        : definitions['ObjectPageHeader<GenericActions>'];
    transferSettingsAndFlexChanges(
        importParameters,
        objectPageConfig.header,
        schemaDefinition as Definition,
        configParameters
    );
    const headerAction = definitions[DefinitionName.ObjectPageHeaderActions] as Definition;
    if (headerAction) {
        fillActions(
            importParameters,
            headerAction.properties,
            objectPageConfig.header,
            configParameters,
            ActionTypeV2.OPHeader
        );
    }
    objectPageConfig.footer = {};
    const footerAction = definitions[DefinitionName.FooterActions] as Definition;
    if (footerAction) {
        fillActions(
            importParameters,
            footerAction.properties,
            objectPageConfig.footer,
            configParameters,
            ActionTypeV2.OPFooter
        );
    }

    // Transfer Layout information
    objectPageConfig.layout = configParameters.factory.createInstance(PageTypeV2.ObjectPage, 'ObjectPageLayout');
    transferSettingsAndFlexChanges(
        importParameters,
        objectPageConfig.layout,
        definitions[DefinitionName.ObjectPageLayout] as Definition,
        configParameters
    );
    if (Object.keys(objectPageConfig.layout).length === 0) {
        delete objectPageConfig.layout;
    }

    // Fill header sections and subsections
    objectPageConfig.header.sections = {};
    const headerSections = definitions[DefinitionName.HeaderSections] as Definition;
    if (headerSections) {
        const manifestSettings = configParameters.v2Page.component?.settings;
        fillSectionsInConfig(
            importParameters,
            headerSections.properties,
            manifestSettings,
            objectPageConfig.header.sections,
            configParameters
        );
    }
    if (Object.keys(objectPageConfig.header.sections).length === 0) {
        delete objectPageConfig.header.sections;
    }
    // Fill sections and subsections
    objectPageConfig.sections = {};
    const sections = definitions[DefinitionName.Sections] as Definition;
    if (sections) {
        const manifestSettings = configParameters.v2Page.component?.settings;
        fillSectionsInConfig(
            importParameters,
            sections.properties,
            manifestSettings,
            objectPageConfig.sections,
            configParameters
        );
    }

    // Find custom sections
    const customSections = findCustomSectionExtensions(importParameters.manifest, importParameters.entitySet);
    fillCustomSectionsInConfig(customSections, objectPageConfig, configParameters, definitions, importParameters);

    return objectPageConfig;
}

/**
 * Creates the configuration (file content) for a V2 object page.
 *
 * @param importParameters  - object comprising all input data
 * @returns - the object page configuration (JSON)
 */
export function createObjectPageConfigV2(
    importParameters: ImportObjectPageV2Parameters
): v2.ObjectPageConfigV2 | undefined {
    // Initialize i18next
    initI18n();

    const v2Pages = getV2Pages(importParameters);
    if (!v2Pages) {
        return;
    }
    const entitySet = importParameters.entitySet;
    const pageKeys: string[] = [];
    const v2Page: v2.SapUiAppPageV2 = findObjectPageV2(v2Pages, importParameters.entitySet, pageKeys);
    checkV2Page(v2Page, importParameters.logger, entitySet);
    if (!v2Page) {
        return;
    }
    const flexChangesForTransfer = prepareFlexChanges(importParameters.flex, entitySet);

    const factory = new Factory();

    //Fill config.json with manifest settings and flex changes
    return fillConfig(importParameters, { pageKeys, flexChanges: flexChangesForTransfer, v2Page, factory });
}
